/**
 * The Map page's view: fitted to the markers until the user takes it over.
 *
 * Fits when the markers first arrive and when the map is asked to show
 * something different (see utils/mapFit.js). Once the user pans or zooms,
 * nothing moves the map again — not the one-minute refresh, not picking an
 * engineer, not a layer or a filter — until they press Fit all, which sits
 * under the zoom buttons.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { shouldAutoFit } from '../utils/mapFit';

// Unanimated on purpose. Leaflet fires the same movestart for a fit as for the
// user's own pan or zoom; without animation all of a fit's events fire before
// fitBounds returns, so a flag set around the call tells them apart. An
// animated zoom starts a frame later, where it looks just like a pinch.
const FIT_OPTIONS = { padding: [50, 50], maxZoom: 10, animate: false };

function MapAutoFit({ positions, scopeKey }) {
  const map = useMap();
  // Read at fit time, never an effect dependency: the page builds a new array
  // every render, and depending on it is what re-fitted the map every render.
  const positionsRef = useRef(positions);
  positionsRef.current = positions;
  const view = useRef({ userMoved: false, fitting: false, fittedScopeKey: null });
  const [controlContainer, setControlContainer] = useState(null);
  const hasPositions = positions.length > 0;

  const fitToMarkers = useCallback(() => {
    if (positionsRef.current.length === 0) return;
    view.current.fitting = true;
    try {
      map.fitBounds(positionsRef.current, FIT_OPTIONS);
    } finally {
      view.current.fitting = false;
    }
  }, [map]);

  // Any other move is the user's: a drag, the wheel, a pinch, the zoom
  // buttons, the keyboard, or the map panning to show a popup they opened.
  useEffect(() => {
    const onMoveStart = () => {
      if (!view.current.fitting) view.current.userMoved = true;
    };
    map.on('movestart', onMoveStart);
    return () => map.off('movestart', onMoveStart);
  }, [map]);

  useEffect(() => {
    const state = view.current;
    if (!shouldAutoFit({
      hasPositions,
      userMoved: state.userMoved,
      scopeKey,
      fittedScopeKey: state.fittedScopeKey
    })) return;
    state.fittedScopeKey = scopeKey;
    fitToMarkers();
  }, [hasPositions, scopeKey, fitToMarkers]);

  // A Leaflet control, so Fit all stacks under the zoom buttons and pressing
  // it never starts a drag of the map underneath.
  useEffect(() => {
    const control = L.control({ position: 'topleft' });
    control.onAdd = () => {
      const container = L.DomUtil.create('div', 'leaflet-bar map-fit-all');
      L.DomEvent.disableClickPropagation(container);
      return container;
    };
    control.addTo(map);
    setControlContainer(control.getContainer());
    return () => control.remove();
  }, [map]);

  // Hands the view back to the map: later changes of layer or filter refit.
  const fitAll = () => {
    view.current.userMoved = false;
    view.current.fittedScopeKey = scopeKey;
    fitToMarkers();
  };

  if (!controlContainer) return null;
  return createPortal(
    <button
      type="button"
      onClick={fitAll}
      disabled={!hasPositions}
      title="Zoom out to show everything on the map"
    >
      Fit all
    </button>,
    controlContainer
  );
}

export default MapAutoFit;
